/* @flow */
import * as React from 'react';
import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';

import type { TranslatorProps } from 'react-i18next';

import type TypeForm from 'forms/_.extend';

import Loading from 'components/Loading/Loading';

import * as LocationApi from 'api/Location/LocationApi';
import { apiService } from 'services/ServiceRegistry';

import SiteAddress from 'components/SiteAddress/SiteAddress';
import ElectroCheckList from 'forms/components/inputs/ElectroCheckList/ElectroCheckList';
import ActionDrawerFormButtons from 'views/ActionDrawer/components/ActionDrawerFormButtons/ActionDrawerFormButtons';
import ActionDrawerBlock from 'views/ActionDrawer/components/ActionDrawerBlock/ActionDrawerBlock';
import ActionDrawerTable from 'views/ActionDrawer/components/ActionDrawerTable/ActionDrawerTable';

import endpoints from 'api/endpoints';

import styles from '../../../../ActionDrawer.module.scss';

type TypeProps = {|
  location: Object,
  form: TypeForm,
  accountId: string,
|} & TranslatorProps;

@translate()
@inject('accountStore')
@observer
export default class AssignUsersForm extends React.Component<*> {
  @observable users: Array<Object> = [];

  @observable loaded: boolean = false;

  async loadUsers(role: string): Promise<void> {
    const { location }: TypeProps = this.props;

    this.loaded = false;

    try {
      this.users = await apiService.getResource(`locations/${location.id}/users`);
    } catch (err) {
      console.log(err);
    }

    this.loaded = true;
  }

  componentDidMount() {
    this.loadUsers();
  }

  render(): React.Node {
    const { t, location, form, accountId, accountStore }: TypeProps = this.props;

    const user: Object = JSON.parse(localStorage.getItem('user'));

    const locationDetails: Object = location.hierarchy ? location : accountStore.locationDetails;

    let details: Array<*> = [];

    if (locationDetails) {
      details = [
        [
          t('general:company'),
          locationDetails.company ? locationDetails.company.name : locationDetails.company_name,
        ],
      ];

      if (locationDetails.hierarchy.site) {
        if (locationDetails.hierarchy.site.name) {
          details.push([t('general:locations.labels.site'), locationDetails.hierarchy.site.name]);
        }

        if (locationDetails.hierarchy.site.address) {
          details.push([
            t('general:locations.labels.site_address'),
            <SiteAddress key="site_address" {...locationDetails.hierarchy.site.address} />,
          ]);
        }
      }

      if (locationDetails.hierarchy.building && locationDetails.hierarchy.building.name) {
        details.push([
          t('general:locations.labels.building_transport'),
          locationDetails.hierarchy.building.name,
        ]);
      }

      if (locationDetails.hierarchy.room && locationDetails.hierarchy.room.name) {
        details.push([t('general:locations.labels.lab_room'), locationDetails.hierarchy.room.name]);
      }

      if (locationDetails.hierarchy.workspace && locationDetails.hierarchy.workspace.name) {
        details.push([
          t('general:locations.labels.system_workspace'),
          locationDetails.hierarchy.workspace.name,
        ]);
      }
    }

    if (!accountStore.locationDetails) {
      return <Loading />;
    }

    return (
      <React.Fragment>
        <div className={styles['action-drawer-content']}>
          <ActionDrawerBlock
            divider={true}
            gutters={true}
            heading={t('general:locations.labels.details')}
          >
            <ActionDrawerTable data={details} />
          </ActionDrawerBlock>
          {this.loaded && (
            <ElectroCheckList
              field={form.$('assignUsers.users')}
              heading={form.$('assignUsers').label}
              endpoint={`${endpoints.accounts}/${accountId}/users`}
              enableSearch={true}
              searchKeys={['first_name', 'last_name']}
              exclude={this.users.map((cc: Object) => cc.id)}
              customResponse={(response: *) => {
                let arr: Array<Object> = [];

                for (const key: string in response) {
                  if (Object.prototype.hasOwnProperty.call(response, key)) {
                    arr = arr.concat(response[key]);
                  }
                }

                return arr;
              }}
              primaryLiteral={(item: Object) => {
                return item.id === user.id ? (
                  <React.Fragment>
                    <strong>Me</strong>&nbsp;({`${item.first_name} ${item.last_name}`})
                  </React.Fragment>
                ) : (
                  `${item.first_name} ${item.last_name}`
                );
              }}
              secondaryLiteral={(item: Object) => (
                <React.Fragment>
                  Current role: <strong>{t(`general:roles.${item.role_id}`)}</strong>
                </React.Fragment>
              )}
            />
          )}
        </div>
        <ActionDrawerFormButtons form={form} submitTxt={t('general:actions.assign_user_plural')} />
      </React.Fragment>
    );
  }
}
